import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Check, RefreshCw, ChevronLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const branchen = [
  { value: 'handwerk', label: 'Handwerk' },
  { value: 'gastronomie', label: 'Gastronomie' },
  { value: 'einzelhandel', label: 'Einzelhandel' },
  { value: 'dienstleistung', label: 'Dienstleistung' },
  { value: 'gesundheit', label: 'Gesundheit' },
  { value: 'bildung', label: 'Bildung' },
  { value: 'immobilien', label: 'Immobilien' },
  { value: 'technologie', label: 'Technologie' },
  { value: 'andere', label: 'Andere' }
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1 Data
  const [firmenname, setFirmenname] = useState('');
  const [branche, setBranche] = useState('');
  const [standort, setStandort] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ziele, setZiele] = useState([]);

  // Step 2 Data
  const [headlines, setHeadlines] = useState(['', '', '', '', '']);
  const [descriptions, setDescriptions] = useState(['', '']);
  const [werbestandorte, setWerbestandorte] = useState('');
  const [expertOptimization, setExpertOptimization] = useState(false);

  // Step 3 Data
  const [budget, setBudget] = useState(300);
  const [budgetInput, setBudgetInput] = useState('300');
  const [laufzeit, setLaufzeit] = useState('monatlich');
  const [agbAccepted, setAgbAccepted] = useState(false);

  // Push begin_onboarding event to dataLayer for GTM tracking (on component mount)
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'begin_onboarding'
    });
  }, []);

  // Push onboarding_step event to dataLayer whenever step changes
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'onboarding_step',
      step: step
    });
  }, [step]);

  const handleZielToggle = (ziel) => {
    if (ziele.includes(ziel)) {
      setZiele(ziele.filter(z => z !== ziel));
    } else {
      setZiele([...ziele, ziel]);
    }
  };

  const handleStep1Next = async () => {
    if (!firmenname || !branche || !standort || !beschreibung || !websiteUrl || !phoneNumber || ziele.length === 0) {
      toast.error('Bitte fülle alle Felder aus');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/onboarding/generate-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firmenname,
          branche,
          standort,
          beschreibung,
          website_url: websiteUrl,
          phone_number: phoneNumber,
          ziele
        })
      });

      if (!response.ok) throw new Error('Fehler beim Generieren der Anzeigentexte');

      const data = await response.json();
      setHeadlines(data.headlines);
      setDescriptions(data.descriptions);
      setStep(2);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Generieren der Anzeigentexte');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateHeadlines = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/onboarding/generate-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firmenname,
          branche,
          standort,
          beschreibung,
          website_url: websiteUrl,
          phone_number: phoneNumber,
          ziele
        })
      });

      if (!response.ok) throw new Error('Fehler beim Regenerieren');

      const data = await response.json();
      setHeadlines(data.headlines);
      toast.success('Neue Anzeigentitel generiert!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Regenerieren der Anzeigentitel');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateDescriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/onboarding/generate-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firmenname,
          branche,
          standort,
          beschreibung,
          website_url: websiteUrl,
          phone_number: phoneNumber,
          ziele
        })
      });

      if (!response.ok) throw new Error('Fehler beim Regenerieren');

      const data = await response.json();
      setDescriptions(data.descriptions);
      toast.success('Neue Anzeigenbeschreibungen generiert!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Regenerieren der Anzeigenbeschreibungen');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Next = () => {
    const validHeadlines = headlines.filter(h => h.trim().length > 0);
    const validDescriptions = descriptions.filter(d => d.trim().length > 0);
    
    if (validHeadlines.length < 3) {
      toast.error('Bitte gib mindestens 3 Headlines ein');
      return;
    }
    
    if (validDescriptions.length < 1) {
      toast.error('Bitte gib mindestens 1 Beschreibung ein');
      return;
    }

    if (!werbestandorte.trim()) {
      toast.error('Bitte gib mindestens einen Werbestandort an');
      return;
    }
    
    setStep(3);
  };

  const handleComplete = async () => {
    if (!agbAccepted) {
      toast.error('Bitte stimme den Bedingungen zu, um fortzufahren');
      return;
    }

    if (!budget || budget <= 0) {
      toast.error('Bitte gib ein gültiges Budget ein');
      return;
    }

    if (budget < 100) {
      toast.error('Das Mindestbudget beträgt 100 €');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const managementFee = budget * 0.20;
      const expertOptimizationFee = expertOptimization ? 29 : 0;
      const totalAmount = budget + managementFee + expertOptimizationFee;
      
      const response = await fetch(`${BACKEND_URL}/api/onboarding/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          budget,
          laufzeit,
          expert_optimization: expertOptimization,
          origin_url: window.location.origin,
          campaign_data: {
            firmenname,
            branche,
            standort,
            beschreibung,
            website_url: websiteUrl,
            phone_number: phoneNumber,
            ziele,
            werbestandorte,
            headlines: headlines.filter(h => h.trim().length > 0),
            descriptions: descriptions.filter(d => d.trim().length > 0),
            budget,
            laufzeit
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Fehler beim Erstellen der Checkout-Session';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Push payment_started event to dataLayer before Stripe redirect
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'payment_started'
      });
      
      window.location.href = data.url;
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Fehler beim Abschluss');
    } finally {
      setLoading(false);
    }
  };

  const managementFee = budget * 0.20;
  const totalAmount = budget + managementFee;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Top Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="AdSimplify Logo"
                className="h-8 w-8 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">AdSimplify</span>
            </div>

            {/* Abbrechen Button */}
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-gray-700 hover:text-gray-900"
            >
              <X className="w-5 h-5 mr-2" />
              Abbrechen
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Step 1 */}
            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= 1 ? 'bg-[#0040FF] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 1 ? <Check className="w-5 h-5" /> : '1'}
              </div>
              <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:block">Unternehmen</span>
            </div>

            {/* Line 1 */}
            <div className={`flex-1 h-1 mx-2 rounded transition-all ${
              step >= 2 ? 'bg-[#0040FF]' : 'bg-gray-200'
            }`} />

            {/* Step 2 */}
            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= 2 ? 'bg-[#0040FF] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 2 ? <Check className="w-5 h-5" /> : '2'}
              </div>
              <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:block">Anzeigentexte</span>
            </div>

            {/* Line 2 */}
            <div className={`flex-1 h-1 mx-2 rounded transition-all ${
              step >= 3 ? 'bg-[#0040FF]' : 'bg-gray-200'
            }`} />

            {/* Step 3 */}
            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= 3 ? 'bg-[#0040FF] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                3
              </div>
              <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:block">Budget</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Business Info */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm">
              <div className="mb-8 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Erzähl uns von deinem Unternehmen
                </h2>
                <p className="text-gray-600">
                  Diese Infos helfen uns, perfekte Anzeigentexte zu erstellen
                </p>
              </div>

              <div className="space-y-6">
                {/* Firmenname */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Firmenname *
                  </label>
                  <Input
                    type="text"
                    value={firmenname}
                    onChange={(e) => setFirmenname(e.target.value)}
                    placeholder="z.B. Müller Bäckerei"
                    className="h-11"
                  />
                </div>

                {/* Branche */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Branche *
                  </label>
                  <Select value={branche} onValueChange={setBranche}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Branche wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {branchen.map(b => (
                        <SelectItem key={b.value} value={b.value}>
                          {b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Standort */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standort (PLZ, Stadt) *
                  </label>
                  <Input
                    type="text"
                    value={standort}
                    onChange={(e) => setStandort(e.target.value)}
                    placeholder="z.B. 1010 Wien"
                    className="h-11"
                  />
                </div>

                {/* Beschreibung */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Beschreibe dein Unternehmen *
                  </label>
                  <Textarea
                    value={beschreibung}
                    onChange={(e) => setBeschreibung(e.target.value)}
                    placeholder="z.B. Traditionelle Bäckerei mit 30 Jahren Erfahrung..."
                    rows={4}
                  />
                </div>

                {/* Website URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webseite URL *
                  </label>
                  <Input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://www.meine-firma.at"
                    className="h-11"
                  />
                </div>

                {/* Telefonnummer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefonnummer *
                  </label>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+43 1 1234567"
                    className="h-11"
                  />
                </div>

                {/* Ziele */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Ziel (Mehrfachauswahl) *
                  </label>
                  <div className="space-y-3">
                    {['Mehr Anrufe', 'Mehr Buchungen', 'Mehr Besucher auf Website'].map(ziel => (
                      <div
                        key={ziel}
                        className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleZielToggle(ziel)}
                      >
                        <Checkbox
                          checked={ziele.includes(ziel)}
                          onCheckedChange={() => handleZielToggle(ziel)}
                        />
                        <label className="text-sm font-medium cursor-pointer flex-1">
                          {ziel}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleStep1Next}
                  disabled={loading}
                  className="w-full h-11 bg-[#0040FF] hover:bg-[#0033CC] text-white font-medium"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generiere Texte...
                    </>
                  ) : (
                    'Weiter zu Textvorschlägen →'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Ad Texts */}
        {step === 2 && (
          <div>
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Deine Anzeige – automatisch erstellt
              </h2>
              <p className="text-gray-600">
                Bearbeite die Texte oder generiere neue mit KI
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Form */}
              <div className="space-y-6">
                {/* Headlines Card */}
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-semibold text-gray-900">
                      Anzeigentitel (max. 30 Zeichen)
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerateHeadlines}
                      disabled={loading}
                      className="text-[#0040FF] border-[#0040FF] hover:bg-[#E6EDFF]"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Neu
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {headlines.map((headline, index) => (
                      <div key={index}>
                        <Input
                          type="text"
                          value={headline}
                          onChange={(e) => {
                            const newHeadlines = [...headlines];
                            newHeadlines[index] = e.target.value.slice(0, 30);
                            setHeadlines(newHeadlines);
                          }}
                          placeholder={`Titel ${index + 1}`}
                          maxLength={30}
                          className="h-10"
                        />
                        <div className="flex justify-between mt-1 px-1">
                          <span className="text-xs text-gray-500">#{index + 1}</span>
                          <span className={`text-xs ${
                            headline.length > 25 ? 'text-red-600 font-medium' : 'text-gray-400'
                          }`}>
                            {headline.length}/30
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Descriptions Card */}
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-semibold text-gray-900">
                      Anzeigenbeschreibung (max. 90 Zeichen)
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerateDescriptions}
                      disabled={loading}
                      className="text-[#0040FF] border-[#0040FF] hover:bg-[#E6EDFF]"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Neu
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {descriptions.map((desc, index) => (
                      <div key={index}>
                        <Textarea
                          value={desc}
                          onChange={(e) => {
                            const newDescriptions = [...descriptions];
                            newDescriptions[index] = e.target.value.slice(0, 90);
                            setDescriptions(newDescriptions);
                          }}
                          placeholder={`Beschreibung ${index + 1}`}
                          maxLength={90}
                          rows={2}
                        />
                        <div className="flex justify-between mt-1 px-1">
                          <span className="text-xs text-gray-500">#{index + 1}</span>
                          <span className={`text-xs ${
                            desc.length > 80 ? 'text-red-600 font-medium' : 'text-gray-400'
                          }`}>
                            {desc.length}/90
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Werbestandorte */}
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Werbestandorte (Städte oder Dörfer) *
                  </label>
                  <Input
                    type="text"
                    value={werbestandorte}
                    onChange={(e) => setWerbestandorte(e.target.value)}
                    placeholder="z.B. Wien, Graz, Salzburg"
                    className="h-11"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Wo sollen deine Anzeigen geschaltet werden?
                  </p>
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-11"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  <Button
                    onClick={handleStep2Next}
                    className="flex-1 h-11 bg-[#0040FF] hover:bg-[#0033CC] text-white"
                  >
                    Weiter zu Budget →
                  </Button>
                </div>
              </div>

              {/* Right: Preview & Upsell */}
              <div className="space-y-6 lg:sticky lg:top-8 lg:self-start">
                {/* Google Ad Preview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Vorschau: Deine Anzeige</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg p-4 bg-white">
                      {/* Ad Badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="default" className="text-xs px-2 py-0.5 bg-gray-900">Anzeige</Badge>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-600">{firmenname || 'example.com'}</span>
                      </div>
                      
                      {/* Title */}
                      <h4 className="text-lg text-[#1a0dab] hover:underline cursor-pointer font-normal mb-1">
                        {headlines.filter(h => h.trim()).slice(0, 3).join(' - ') || 'Ihre Anzeigentitel'}
                      </h4>
                      
                      {/* URL */}
                      <div className="mb-2">
                        <div className="flex items-center gap-1 text-sm">
                          <span className="text-gray-700 font-medium">{standort || 'Wien'}</span>
                          <span className="text-gray-400">›</span>
                          <span className="text-gray-700">{branche || 'Dienstleistung'}</span>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <div className="text-sm text-gray-600">
                        {descriptions[0] || 'Ihre Anzeigenbeschreibung'}
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-gray-700">
                        <strong>💡 Tipp:</strong> Google zeigt unterschiedliche Kombinationen deiner Texte an.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Upsell Card */}
                <Card 
                  className={`cursor-pointer transition-all ${
                    expertOptimization 
                      ? 'border-[#0040FF] border-2 shadow-lg' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setExpertOptimization(!expertOptimization)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Experten-Optimierung
                        </h4>
                        <p className="text-sm text-gray-600">
                          Professionell optimierte Texte für maximale Klickrate
                        </p>
                      </div>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                        expertOptimization ? 'bg-[#0040FF] border-[#0040FF]' : 'border-gray-300'
                      }`}>
                        {expertOptimization && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className="text-sm font-medium text-gray-700">Einmalig</span>
                      <span className="text-lg font-bold text-[#0040FF]">29 €</span>
                    </div>
                    
                    {expertOptimization && (
                      <div className="mt-3 p-2 bg-[#E6EDFF] rounded text-center">
                        <p className="text-xs font-medium text-[#0040FF]">
                          ✓ Wird beim Checkout hinzugefügt
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Budget */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm">
              <div className="mb-8 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Wie viel möchtest du investieren?
                </h2>
                <p className="text-gray-600">
                  Wähle dein Budget und die Laufzeit
                </p>
              </div>

              <div className="space-y-6">
                {/* Budget Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Werbebudget (in Euro) *
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={budgetInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setBudgetInput(value);
                          setBudget(value === '' ? 0 : Number(value));
                        }
                      }}
                      onBlur={() => {
                        if (budgetInput === '' || Number(budgetInput) === 0) {
                          setBudgetInput('300');
                          setBudget(300);
                        }
                      }}
                      className="h-16 text-2xl font-bold pr-12"
                      placeholder="300"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl font-bold text-gray-400">€</span>
                  </div>
                  
                  {budget < 300 && budget > 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>⚠️ Hinweis:</strong> Wir empfehlen mindestens 300 € für optimale Ergebnisse.
                      </p>
                    </div>
                  )}
                </div>

                {/* Laufzeit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Laufzeit *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => setLaufzeit('monatlich')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        laufzeit === 'monatlich'
                          ? 'border-[#0040FF] bg-[#E6EDFF]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold text-gray-900 mb-1">Monatlich</div>
                        <div className="text-xs text-gray-600">Automatische Verlängerung</div>
                        {laufzeit === 'monatlich' && (
                          <Check className="w-5 h-5 text-[#0040FF] mx-auto mt-2" />
                        )}
                      </div>
                    </div>
                    
                    <div
                      onClick={() => setLaufzeit('einmalig')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        laufzeit === 'einmalig'
                          ? 'border-[#0040FF] bg-[#E6EDFF]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold text-gray-900 mb-1">Einmalig</div>
                        <div className="text-xs text-gray-600">Keine Verlängerung</div>
                        {laufzeit === 'einmalig' && (
                          <Check className="w-5 h-5 text-[#0040FF] mx-auto mt-2" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Werbebudget:</span>
                    <span className="font-medium">{budget.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Management-Fee (20%):</span>
                    <span className="font-medium">{managementFee.toFixed(2)} €</span>
                  </div>
                  
                  {expertOptimization && (
                    <div className="flex justify-between text-sm pt-3 border-t">
                      <span className="text-gray-700 font-medium">Experten-Optimierung:</span>
                      <span className="font-medium text-[#0040FF]">29.00 €</span>
                    </div>
                  )}
                  
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-bold text-gray-900">Gesamtbetrag:</span>
                    <span className="font-bold text-[#0040FF] text-xl">
                      {(totalAmount + (expertOptimization ? 29 : 0)).toFixed(2)} €
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 text-center">
                    {laufzeit === 'monatlich' ? '* Monatlich abgerechnet' : '* Einmalige Zahlung'}
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border-l-4 border-[#0040FF] p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-[#0040FF]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-900">
                        <strong>Nächste Schritte:</strong> Nach der Zahlung wird deine Anzeige von unseren Experten geprüft und optimiert. Du erhältst eine E-Mail-Benachrichtigung, sobald die Anzeige live ist.
                      </p>
                    </div>
                  </div>
                </div>

                {/* AGB Checkbox */}
                <div className="flex items-start space-x-3 p-4 border rounded-lg">
                  <Checkbox
                    id="agb"
                    checked={agbAccepted}
                    onCheckedChange={setAgbAccepted}
                  />
                  <label htmlFor="agb" className="text-sm text-gray-700 cursor-pointer flex-1">
                    Ich stimme zu, dass AdSimplify meine Anzeige verwaltet und die Kosten gemäß der gewählten Laufzeit abgerechnet werden. *
                  </label>
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-11"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="flex-1 h-11 bg-[#0040FF] hover:bg-[#0033CC] text-white font-medium"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Verarbeite...
                      </>
                    ) : (
                      `Anzeige starten für ${(totalAmount + (expertOptimization ? 29 : 0)).toFixed(2)} €`
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
